import { motion } from 'framer-motion'
import { TrendingUp, Sparkles, Target, AlertCircle, Lock, Zap } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  description: string
  features: string[]
  featured?: boolean
  badge?: string
  gradientClass?: string
}

const FeatureCard = ({ icon, title, description, features, featured, badge, gradientClass = 'gradient-primary' }: FeatureCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`relative p-8 rounded-3xl border-2 transition-all hover:-translate-y-2 hover:shadow-2xl ${
        featured
          ? `${gradientClass} text-white border-transparent`
          : 'bg-gray-900 text-white border-gray-800 hover:border-primary'
      }`}
    >
      {badge && (
        <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-white">
          {badge}
        </div>
      )}

      <div
        className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 ${
          featured ? 'bg-white/20' : 'bg-blue-50'
        }`}
      >
        {icon}
      </div>

      <h3 className="text-2xl font-bold mb-3">{title}</h3>
      <p className={`mb-6 leading-relaxed ${featured ? 'text-white/90' : 'text-gray-300'}`}>
        {description}
      </p>

      <ul className="space-y-3">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2">
            <span className={`text-lg ${featured ? 'text-white' : 'text-green-500'}`}>✓</span>
            <span className={`text-sm ${featured ? 'text-white/90' : 'text-gray-200'}`}>
              {feature}
            </span>
          </li>
        ))}
      </ul>
    </motion.div>
  )
}

const Features = () => {
  const { t } = useLanguage()
  
  const features = [
    {
      icon: <TrendingUp className="w-6 h-6 text-primary" />,
      title: t('features.multiBanking.title'),
      description: t('features.multiBanking.description'),
      features: [
        t('features.multiBanking.feature1'),
        t('features.multiBanking.feature2'),
        t('features.multiBanking.feature3'),
      ],
    },
    {
      icon: <Sparkles className="w-6 h-6 text-white" />,
      title: t('features.charts.title'),
      description: t('features.charts.description'),
      features: [
        t('features.charts.feature1'),
        t('features.charts.feature2'),
        t('features.charts.feature3'),
      ],
      featured: true,
      badge: t('features.charts.badge'),
    },
    {
      icon: <Sparkles className="w-6 h-6 text-white" />,
      title: t('features.aiTips.title'),
      description: t('features.aiTips.description'),
      features: [
        t('features.aiTips.feature1'),
        t('features.aiTips.feature2'),
        t('features.aiTips.feature3'),
      ],
      featured: true,
      badge: t('features.aiTips.badge'),
      gradientClass: 'gradient-hot',
    },
    {
      icon: <Target className="w-6 h-6 text-green-500" />,
      title: t('features.savings.title'),
      description: t('features.savings.description'),
      features: [
        t('features.savings.feature1'),
        t('features.savings.feature2'),
        t('features.savings.feature3'),
      ],
    },
    {
      icon: <AlertCircle className="w-6 h-6 text-red-500" />,
      title: t('features.dispo.title'),
      description: t('features.dispo.description'),
      features: [
        t('features.dispo.feature1'),
        t('features.dispo.feature2'),
        t('features.dispo.feature3'),
      ],
    },
    {
      icon: <Lock className="w-6 h-6 text-purple-500" />,
      title: t('features.security.title'),
      description: t('features.security.description'),
      features: [
        t('features.security.feature1'),
        t('features.security.feature2'),
        t('features.security.feature3'),
      ],
    },
  ]

  return (
    <section id="features" className="py-12 lg:py-16 bg-black text-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6"
          >
            <Zap className="h-5 w-5 text-blue-400" />
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">
              {t('features.label')}
            </span>
          </motion.div>

          <h2 className="text-5xl font-bold mb-4">
            {t('features.title')} <span className="text-gradient">{t('features.titleGradient')}</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('features.description')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>
      </div>
    </section>
  )
}

export default Features
